import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

/**
 * Content for footer wells
 * Supports React nodes for maximum flexibility
 */
export interface FooterContent {
  /** Content for left well (page-specific controls) */
  left?: React.ReactNode;
  /** Content for right well (view commands) */
  right?: React.ReactNode;
}

/**
 * Footer context value
 * Provides API for pages to publish content
 */
export interface FooterContextValue {
  /** Current footer content */
  content: FooterContent;

  /** Set content for left well */
  setLeftContent: (node: React.ReactNode) => void;

  /** Set content for right well */
  setRightContent: (node: React.ReactNode) => void;

  /** Set both wells at once */
  setContent: (content: FooterContent) => void;

  /** Clear all footer content (resets to empty state) */
  clearContent: () => void;
}

const FooterContext = createContext<FooterContextValue | null>(null);

export interface FooterProviderProps {
  /** App tree to provide footer context to */
  children: React.ReactNode;
}

/**
 * Provides footer content context to entire app
 * Must wrap the application root
 */
export function FooterProvider({ children }: FooterProviderProps): JSX.Element {
  const [content, setContentState] = useState<FooterContent>({});

  const setLeftContent = useCallback((node: React.ReactNode) => {
    setContentState((prev) => ({ ...prev, left: node }));
  }, []);

  const setRightContent = useCallback((node: React.ReactNode) => {
    setContentState((prev) => ({ ...prev, right: node }));
  }, []);

  const setContent = useCallback((newContent: FooterContent) => {
    setContentState(newContent);
  }, []);

  const clearContent = useCallback(() => {
    setContentState({});
  }, []);

  const value = useMemo<FooterContextValue>(
    () => ({
      content,
      setLeftContent,
      setRightContent,
      setContent,
      clearContent,
    }),
    [content, setLeftContent, setRightContent, setContent, clearContent]
  );

  return <FooterContext.Provider value={value}>{children}</FooterContext.Provider>;
}

/**
 * Hook to access footer content context
 * Provides API for publishing content to footer wells
 *
 * @throws Error if used outside FooterProvider
 *
 * @example
 * function MyPage() {
 *   const { setRightContent, clearContent } = useFooterContent();
 *
 *   useEffect(() => {
 *     setRightContent(<ViewControls />);
 *     return () => clearContent();
 *   }, [setRightContent, clearContent]);
 * }
 */
export function useFooterContent(): FooterContextValue {
  const context = useContext(FooterContext);
  if (!context) {
    throw new Error('useFooterContent must be used within FooterProvider');
  }
  return context;
}
